import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { canAccess, normalizeRole, ROLES } from '../config/permissions';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          // Verify token and get user info
          const response = await api.get('/auth/me');
          setUser(response.data);
          setIsAuthenticated(true);

          // Get tenant info
          const tenantResponse = await api.get('/tenants/me');
          setTenant(tenantResponse.data);
        } catch (error) {
          // Token is invalid or expired
          console.error('Auth initialization failed:', error);
          localStorage.removeItem('access_token');
          setUser(null);
          setTenant(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Refetch identity when the tab regains focus. A super-admin can move a user
  // to another tenant server-side; without this the sidebar tenant/role label
  // stays stale (the /tenants/me + /auth/me fetch otherwise only runs on mount
  // or explicit login). Guarded by a valid token so logged-out tabs stay quiet.
  useEffect(() => {
    const refetchIdentity = async () => {
      if (!localStorage.getItem('access_token')) return;
      try {
        const [meRes, tenantRes] = await Promise.all([
          api.get('/auth/me'),
          api.get('/tenants/me'),
        ]);
        setUser(meRes.data);
        setTenant(tenantRes.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Non-fatal: keep the last-known identity rather than logging the user
        // out on a transient refetch failure.
        console.warn('Identity refetch on focus failed:', error);
      }
    };
    window.addEventListener('focus', refetchIdentity);
    return () => window.removeEventListener('focus', refetchIdentity);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;

      // Store token
      localStorage.setItem('access_token', access_token);

      // Update state
      setUser(userData);
      setIsAuthenticated(true);

      // Get tenant info
      const tenantResponse = await api.get('/tenants/me');
      setTenant(tenantResponse.data);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const registerTenant = async (tenantData, adminData) => {
    try {
      const response = await api.post('/auth/register-tenant', {
        tenant_data: tenantData,
        admin_user: adminData
      });
      const { access_token, user: userData, tenant: tenantInfo } = response.data;

      // Store token
      localStorage.setItem('access_token', access_token);

      // Update state
      setUser(userData);
      setTenant(tenantInfo);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    setTenant(null);
    setIsAuthenticated(false);
  };

  const googleLogin = async (googleToken) => {
    try {
      const response = await api.post('/auth/google/verify', { token: googleToken });
      const { access_token, user: userData } = response.data;

      // Store token
      localStorage.setItem('access_token', access_token);

      // Update state
      setUser(userData);
      setIsAuthenticated(true);

      // Get tenant info if user has one
      try {
        const tenantResponse = await api.get('/tenants/me');
        setTenant(tenantResponse.data);
      } catch (tenantError) {
        // User might not have a tenant yet (new Google user)
        console.log('No tenant found for user');
      }

      return { success: true };
    } catch (error) {
      console.error('Google login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Google login failed'
      };
    }
  };

  const hasRole = (allowedRoles) => {
    if (!user) return false;
    const normalized = normalizeRole(user.role);
    return allowedRoles.includes(normalized);
  };

  const isAdmin = () => hasRole(['admin', 'super_admin']);
  const isManager = () => hasRole(['admin', 'super_admin', 'manager']);
  const isCaller = () => {
    if (!user) return false;
    return normalizeRole(user.role) === ROLES.CALLER;
  };

  const can = useCallback((featureKey) => {
    if (!user) return false;
    return canAccess(user.role, featureKey);
  }, [user]);

  const value = {
    user,
    tenant,
    loading,
    isAuthenticated,
    login,
    logout,
    registerTenant,
    googleLogin,
    hasRole,
    isAdmin,
    isManager,
    isCaller,
    can,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
